import React, { useState, useEffect, useMemo } from 'react';
import { flexRender, Row } from '@tanstack/react-table';
import { Tr, Td } from '../../../../components/styles/table-style';
import { Product } from '../AddProduct/types';
import { StyledSelect } from './style';
import { formatCurrency } from '../ViewProduct/utils';
import listIcon from '../../../../assets/details.svg';
import editIcon from '../../../../assets/edit-pen.svg';
import trashIcon from '../../../../assets/trash.svg';
import { OptionsImage } from './style';
import ItemsDisplay from './ItemsDisplay';


interface TableRowProps {
  row: Row<Product>;
  handleView: (id: number) => void;
  handleEdit: (id: number) => void;
  handleDeleteClick: (id: number, name: string) => void;
}

const TableRow: React.FC<TableRowProps> = ({ row, handleView, handleEdit, handleDeleteClick }) => {
  const product = row.original;
  const [selectedScriptId, setSelectedScriptId] = useState<number | null>(
    product.scripts && product.scripts.length > 0 ? product.scripts[0].id : null
  );
  const [updateTrigger, setUpdateTrigger] = useState(0);

  useEffect(() => {
    if (product.scripts && product.scripts.length > 0) {
      const hasSelectedScript = product.scripts.some(script => script.id === selectedScriptId);
      if (!hasSelectedScript) {
        setSelectedScriptId(product.scripts[0].id);
      }
    } else if (selectedScriptId) {
      setSelectedScriptId(null);
    }
  }, [product.scripts, selectedScriptId]);

  const selectedScript = useMemo(() => {
    return product.scripts?.find(s => s.id === selectedScriptId) || null;
  }, [product.scripts, selectedScriptId, updateTrigger]);

  const deadline = useMemo(() => {
    if (!selectedScript) return 0;
    const additional = selectedScript.additionalDeadline || 0;
    const servicesTime = selectedScript.totalDeliveryTime || 0;
    return additional + servicesTime;
  }, [selectedScript, updateTrigger]);

  const cost = useMemo(() => {
    return selectedScript?.cost || 0;
  }, [selectedScript, updateTrigger]);
  const price = useMemo(() => cost * 1.3, [cost, updateTrigger]);
  const itemsDisplay = useMemo(() => {
    if (!selectedScript) return 'N/A';
    return selectedScript.items || [];
  }, [selectedScript, updateTrigger]);

  const handleScriptChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newScriptId = Number(e.target.value);
    setSelectedScriptId(newScriptId);
    setUpdateTrigger(prev => prev + 1);
  };

  return (
    <Tr key={`${product.id}-${selectedScriptId}`}>
      {row.getVisibleCells().map(cell => {
        let cellContent;
        switch (cell.column.id) {
          case 'script':
            cellContent = (
              product.scripts && product.scripts.length > 1 ? (
                <StyledSelect
                  value={selectedScriptId || ''}
                  onChange={handleScriptChange}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                >
                  {product.scripts.map(script => (
                    <option key={script.id} value={script.id}>
                      {script.name}
                    </option>
                  ))}
                </StyledSelect>
              ) : (
                selectedScript?.name || 'Nenhum'
              )
            );
            break;
          case 'items':
            cellContent = Array.isArray(itemsDisplay) ? (
              <ItemsDisplay items={itemsDisplay} maxWidth={200} />
            ) : (
              itemsDisplay
            );
            break;
          case 'deadline':
            cellContent = deadline;
            break;
          case 'cost':
            cellContent = formatCurrency(cost);
            break;
          case 'price':
            cellContent = formatCurrency(price);
            break;
          case 'actions':
            cellContent = (
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', paddingRight: '1rem' }}>
                <OptionsImage src={listIcon} alt="Ver detalhes" onClick={() => product.id && handleView(product.id)} title="Ver detalhes" />
                <OptionsImage src={editIcon} alt="Editar" onClick={() => product.id && handleEdit(product.id)} title="Editar" />
                <OptionsImage src={trashIcon} alt="Excluir" onClick={() => product.id && handleDeleteClick(product.id, product.name)} title="Excluir" />
              </div>
            );
            break;
          default:
            cellContent = flexRender(cell.column.columnDef.cell, cell.getContext());
        }
        return <Td key={cell.id}>{cellContent}</Td>;
      })}
    </Tr>
  );
};

export default TableRow; 